import {
    Component, Input, Output, EventEmitter,
    OnChanges, SimpleChanges, AfterViewInit, OnDestroy,
    ElementRef, ViewChild, NgZone
} from '@angular/core';
import { RestApiService } from '@app/shared/services/rest-api.service';
import { DateService } from '@app/shared/services/date.service';
import { environment } from '@env/environment';
import { RESERVATION_STATUS_NAMES } from '@app/shared/constants/reservation-status';
import * as moment from 'moment';
import {
    EcTimelineEnhancer, ZoomLevel, ZOOM_CONFIGS,
    SNAP_DURATION, RESOURCE_AREA_WIDTH_PX, WINDOW_DAYS
} from './ec-timeline-enhancer';

declare const EventCalendar: any;

// ── Public interfaces ────────────────────────────────────────────────────────

export interface CreatedReservation {
    idVehicle: number;
    idStation: number;
    begin: string;           // ISO 'YYYY-MM-DDTHH:mm:ss'
    end: string;
    idAccountTarget?: number;
    comment?: string;
}

/** Emitted on any valid drag-selection, regardless of mode. */
export interface SlotSelection {
    idVehicle: number;
    idStation: number;
    vehicleName: string;
    stationName: string;
    begin: string;           // ISO 'YYYY-MM-DDTHH:mm:ss'
    end: string;
}

// ── Component ────────────────────────────────────────────────────────────────

@Component({
    selector: 'app-reservation-planner',
    templateUrl: './reservation-planner.component.html',
    styleUrls: ['./reservation-planner.component.scss']
})
export class ReservationPlannerComponent implements AfterViewInit, OnChanges, OnDestroy {

    // ── Inputs / Outputs ─────────────────────────────────────────────────────

    @Input() idSocietyVop: number = 0;
    @Input() idAccountTarget?: number;
    /**
     * 'create' (default): a valid drag-selection opens the embedded booking
     *   form and emits reservationCreated on confirm.
     * 'select': the booking form is suppressed; the parent receives slotSelected
     *   and handles the selection itself.
     */
    @Input() mode: 'create' | 'select' = 'create';

    /** Show the "Ajouter un événement" button / create-mode status bar in the toolbar. */
    @Input() showCreateButton: boolean = false;
    /** When set, the status bar is shown instead of the button (e.g. "Ajout de réservation : Jean Dupont — MOD-eden"). */
    @Input() createModeLabel: string = '';

    @Output() reservationCreated = new EventEmitter<CreatedReservation>();
    @Output() slotSelected       = new EventEmitter<SlotSelection>();
    @Output() cancelled          = new EventEmitter<void>();
    @Output() addEventClick      = new EventEmitter<void>();
    @Output() deactivateCreate   = new EventEmitter<void>();

    @ViewChild('calendarEl', { static: false }) calendarEl: ElementRef;

    // ── Calendar internals ───────────────────────────────────────────────────

    private ec: any = null;
    private enhancer: EcTimelineEnhancer | null = null;
    private calendarInitialized = false;
    private readonly PENDING_EVENT_ID = 'pending-reservation';

    // ── Window / navigation state ────────────────────────────────────────────

    /** ISO date of the first day of the loaded 60-day canvas. */
    loadedWindowStart: string = moment().subtract(WINDOW_DAYS / 2, 'days').startOf('day').format('YYYY-MM-DD');

    /** Current zoom level — controls visible range and gridlines. */
    zoomLevel: ZoomLevel = 'week';

    /**
     * ISO date of the calendar date currently visible at the left scroll edge.
     * Updated by the enhancer's scroll listener (via ngZone.run).
     */
    currentDate: string = moment().format('YYYY-MM-DD');

    // ── Data ─────────────────────────────────────────────────────────────────

    vehicles: any[] = [];
    private loadedEvents: any[] = [];
    private multiSociety = false;

    // ── Booking form ─────────────────────────────────────────────────────────

    showForm = false;
    selection: {
        vehicleId: number;
        stationId: number;
        vehicleName: string;
        stationName: string;
    } | null = null;

    private _formBegin = '';
    private _formEnd   = '';

    get formBegin(): string { return this._formBegin; }
    set formBegin(v: string) { this._formBegin = v; }

    get formBeginDate(): string { return this._formBegin ? this._formBegin.split('T')[0] : ''; }
    set formBeginDate(v: string) {
        const time = this.formBeginTime || '00:00';
        this._formBegin = v ? `${v}T${time}` : '';
    }
    get formBeginTime(): string { return this._formBegin ? (this._formBegin.split('T')[1] || '00:00') : ''; }
    set formBeginTime(v: string) {
        const date = this.formBeginDate;
        this._formBegin = date ? `${date}T${v}` : '';
    }

    get formEnd(): string { return this._formEnd; }
    set formEnd(v: string) { this._formEnd = v; }

    get formEndDate(): string { return this._formEnd ? this._formEnd.split('T')[0] : ''; }
    set formEndDate(v: string) {
        const time = this.formEndTime || '00:00';
        this._formEnd = v ? `${v}T${time}` : '';
    }
    get formEndTime(): string { return this._formEnd ? (this._formEnd.split('T')[1] || '00:00') : ''; }
    set formEndTime(v: string) {
        const date = this.formEndDate;
        this._formEnd = date ? `${date}T${v}` : '';
    }

    formComment = '';
    submitError = '';

    // ── Loading / submitting ─────────────────────────────────────────────────

    loading    = true;
    submitting = false;

    constructor(private api: RestApiService, private ngZone: NgZone, private dateService: DateService) {}

    ngAfterViewInit() {
        this.initCalendar();
        this.initEnhancer();
        this.loadAll();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!this.calendarInitialized) { return; }
        if (changes.idSocietyVop) {
            this.applyFilter();
        } else if (changes.idAccountTarget) {
            // Re-render events with updated opacity — no data reload needed.
            this.buildEventsFromData(this.vehicles);
        }
    }

    ngOnDestroy() {
        this.enhancer?.destroy();
        this.enhancer = null;
        if (this.ec) {
            try { this.ec.destroy(); } catch (e) {}
            this.ec = null;
        }
    }

    // ── Calendar initialisation ──────────────────────────────────────────────

    private initCalendar() {
        if (!this.calendarEl || this.calendarInitialized) { return; }

        // Read CSS custom properties once so all EC callbacks close over them.
        const cs = getComputedStyle(this.calendarEl.nativeElement);
        const prop = (name: string) => cs.getPropertyValue(name).trim();
        const eventCodeSize     = prop('--ec-event-code-size');
        const eventNameSize     = prop('--ec-event-name-size');
        const eventNameOpacity  = prop('--ec-event-name-opacity');
        const resourceLabelSize = prop('--ec-resource-label-size');
        const resourceSubSize   = prop('--ec-resource-sub-size');
        const resourceColor     = prop('--ec-resource-color');
        const resourceModelColor = prop('--ec-resource-model-color');
        const resourceLetterSpacing = prop('--ec-resource-letter-spacing');

        const self = this;
        const initialZoom = ZOOM_CONFIGS[this.zoomLevel];

        this.ec = EventCalendar.create(this.calendarEl.nativeElement, {
            // Continuous 60-day canvas — override the default 1-week duration.
            // mergeOpts in EventCalendar's option system does a spread merge,
            // so component, theme, etc. from the plugin defaults are preserved.
            view: 'resourceTimelineWeek',
            views: {
                resourceTimelineWeek: {
                    duration: { days: WINDOW_DAYS }
                }
            },

            date: new Date(this.loadedWindowStart),

            slotDuration: initialZoom.slotDuration,
            snapDuration: SNAP_DURATION,   // fine-grained drag snap, independent of zoom gridlines
            slotWidth: 32,                 // overridden immediately by EcTimelineEnhancer

            headerToolbar: { start: '', center: '', end: '' }, // we render our own toolbar
            resources: [],
            events: [],
            selectable: true,
            nowIndicator: true,
            eventOverlap: false,

            selectConstraint: (info: any) => {
                if (!info.resource?.extendedProps?.isVehicle) { return false; }
                const vehicleId = info.resource.extendedProps.vehicleId;
                const selStart  = info.start.getTime();
                const selEnd    = info.end.getTime();
                return !self.loadedEvents.some((e: any) => {
                    if (e.display === 'background')      { return false; }
                    if (e.id === self.PENDING_EVENT_ID)  { return false; }
                    if (e.resourceId !== vehicleId)      { return false; }
                    return selStart < new Date(e.end).getTime() && selEnd > new Date(e.start).getTime();
                });
            },
            selectBackgroundColor: '#176bc1',
            firstDay: 1,  // Monday week start
            resourceAreaWidth: `${RESOURCE_AREA_WIDTH_PX}px`,

            resourceLabelContent: (info: any) => {
                const res = info.resource;
                if (res.extendedProps?.isStation) {
                    const stationId = res.extendedProps.stationId;
                    const prefix = stationId ? `#${stationId} · ` : '';
                    const labelStyle = `font-family:'Greycliff-Bold',Arial,sans-serif;font-size:${resourceLabelSize};text-transform:uppercase;color:${resourceColor};letter-spacing:${resourceLetterSpacing};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;text-decoration:none;`;
                    const label = stationId
                        ? `<a class="ec-station-name" href="/#/ressource/station/informations/${stationId}" target="_blank" onclick="event.stopPropagation()" style="${labelStyle}">${prefix}${res.title}</a>`
                        : `<span style="${labelStyle}">${res.title}</span>`;
                    return {
                        html: `<div data-station-row="true" style="display:flex;align-items:center;width:100%;padding:2px 12px;text-align:left;">${label}</div>`
                    };
                }
                const plate      = res.title || '';
                const brand      = res.extendedProps?.brand      || '';
                const model      = res.extendedProps?.model      || '';
                const picture    = res.extendedProps?.picture    || '';
                const codeSociety = res.extendedProps?.codeSociety || '';
                const boldFont  = "font-family:'Greycliff-Bold',Arial,sans-serif;";
                const lightFont = "font-family:'Greycliff-Light',Arial,sans-serif;";
                const color     = `color:${resourceColor};`;
                const ellipsis  = 'white-space:nowrap;overflow:hidden;text-overflow:ellipsis;display:block;';
                const thumb = picture
                    ? `<img src="${picture}" style="width:62px;height:47px;object-fit:cover;border-radius:4px;flex-shrink:0;" alt="">`
                    : `<div style="width:62px;height:47px;border-radius:4px;background:#eee;flex-shrink:0;display:flex;align-items:center;justify-content:center;"><img src="/assets/icons/Vehicle.svg" width="20" height="20" alt="" style="opacity:0.35;"></div>`;
                const vehicleId = res.extendedProps?.vehicleId;
                return {
                    html: `<div style="display:flex;align-items:center;gap:8px;padding:2px 0;min-width:0;overflow:hidden;width:100%;text-align:left;">
                        ${thumb}
                        <div style="display:flex;flex-direction:column;line-height:1.25;min-width:0;flex:1;overflow:hidden;">
                            <a class="ec-vehicle-plate" href="/#/ressource/vehicle/informations/${vehicleId}" target="_blank" onclick="event.stopPropagation()" style="${boldFont}font-size:${resourceLabelSize};${color}${ellipsis}text-decoration:none;">${plate}${self.multiSociety && codeSociety ? ` · ${codeSociety}` : ''}</a>
                            <span style="${boldFont}font-size:${resourceSubSize};${color}text-transform:uppercase;${ellipsis}">${brand}</span>
                            <span style="${lightFont}font-size:${resourceSubSize};color:${resourceModelColor};${ellipsis}">${model}</span>
                        </div>
                    </div>`
                };
            },

            eventContent: (info: any) => {
                const ep = info.event.extendedProps;
                if (!ep?.idReservation) {
                    return { html: '<div style="height:100%;width:100%;"></div>' };
                }
                const code      = ep.code     || '';
                const userName  = ep.userName || '';
                const idBooking = ep.idReservation;
                const boldFont  = "font-family:'Greycliff-Bold',Arial,sans-serif;";
                const lightFont = "font-family:'Greycliff-Light',Arial,sans-serif;";
                const testRed    = 'var(--vop-testinfo-red)';
                const codeContent = environment.testMode
                    ? `<span style="color:${testRed};font-size:10px;">(${idBooking})\u00a0</span>${code}<span style="color:${testRed};font-size:10px;"> (${ep.status}\u00a0${RESERVATION_STATUS_NAMES[ep.status] ?? '?'})</span>`
                    : code;
                const codeLink  = (codeContent || code)
                    ? `<a href="/#/reservation/informations/${idBooking}" target="_blank" style="${boldFont}font-size:${eventCodeSize};color:inherit;text-decoration:underline;white-space:nowrap;cursor:pointer;" onclick="event.stopPropagation()">${codeContent}</a>`
                    : '';
                const nameSpan  = userName
                    ? `<div style="${lightFont}font-size:${eventNameSize};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;opacity:${eventNameOpacity};">${userName}</div>`
                    : '';
                return {
                    html: `<div style="padding:1px 4px;overflow:hidden;height:100%;display:flex;flex-direction:column;justify-content:center;">${codeLink}${nameSpan}</div>`
                };
            },

            eventDidMount: (info: any) => {
                const ep = info.event.extendedProps;
                if (!ep?.idReservation) { return; }
                const code      = ep.code     || '';
                const userName  = ep.userName || '';
                const idBooking = ep.idReservation;
                const sameDay   = moment(info.event.start).isSame(moment(info.event.end), 'day');
                const startStr  = moment(info.event.start).format('DD/MM/YYYY HH:mm');
                const endStr    = moment(info.event.end).format(sameDay ? 'HH:mm' : 'DD/MM/YYYY HH:mm');
                const comment     = ep.comment || '';
                const commentLine = comment ? `Commentaire :<br>${comment}` : '';
                const codeLabel   = environment.testMode
                    ? `<span style="color:var(--vop-testinfo-red);">(${idBooking})</span>${code}<span style="color:var(--vop-testinfo-red);"> (${ep.status} ${RESERVATION_STATUS_NAMES[ep.status] ?? '?'})</span>`
                    : code;
                const lines = [codeLabel, userName, `${startStr} – ${endStr}`, commentLine].filter(Boolean);
                if ((window as any).UIkit) {
                    (window as any).UIkit.tooltip(info.el, { title: lines.join('<br>'), pos: 'top' });
                }
            },

            select: (info: any) => {
                self.ngZone.run(() => {
                    if (!info.resource?.extendedProps?.isVehicle) {
                        try { self.ec.unselect(); } catch (e) {}
                        return;
                    }
                    if (info.start < new Date()) {
                        try { self.ec.unselect(); } catch (e) {}
                        return;
                    }
                    self.onSlotSelected(info);
                });
            },

            unselect: () => {
                self.ngZone.run(() => {
                    if (!self.showForm) { self.clearSelection(); }
                });
            }
        });

        this.calendarInitialized = true;
    }

    private initEnhancer() {
        if (!this.ec || !this.calendarEl) { return; }

        this.enhancer = new EcTimelineEnhancer({
            ec: this.ec,
            containerEl: this.calendarEl.nativeElement,
            getState: () => ({
                zoom:        this.zoomLevel,
                showForm:    this.showForm,
                formBegin:   this.formBegin ? this.fromDateTimeLocal(this.formBegin) : undefined,
                windowStart: moment(this.loadedWindowStart).toDate(),
            }),
            onDateChange: (date) => {
                // Called outside Angular zone from the scroll event listener.
                this.ngZone.run(() => {
                    this.currentDate = moment(date).format('YYYY-MM-DD');
                });
            },
            onEdgeApproached: (_date) => {
                // The backend always returns a fixed -30/+30 window from today;
                // this hook is wired up for future use if the API gains range params.
            }
        });
    }

    // ── Data loading ─────────────────────────────────────────────────────────

    private loadAll(restoreDate?: Date) {
        this.loading = true;

        // Reset the window to ±30 days from today on every load.
        this.loadedWindowStart = moment().subtract(WINDOW_DAYS / 2, 'days').startOf('day').format('YYYY-MM-DD');
        const windowStartDate  = moment(this.loadedWindowStart).toDate();

        // Identity resolution is now server-side (SV-92/SV-93) — no society param needed.
        this.api.get('/MI_Galago/WCF_MI_Galago/REST/GetVehiclesReservationsVopV2', {}, true)
            .then((res: any) => {
                this.ngZone.run(() => {
                    this.vehicles = Array.isArray(res) ? res : [];
                    this.vehicles.forEach(v => {
                        v.reservations = this.dateService.convertAlltoLocal(v.reservations || []);
                    });

                    // Reanchor the canvas start date (in case of a future reload).
                    if (this.ec) { this.ec.setOption('date', windowStartDate); }

                    this.applyFilter();
                    this.loading = false;

                    // After render, recalc slot width then restore scroll position.
                    // Use restoreDate if provided (e.g. after creation), otherwise snap to today.
                    setTimeout(() => {
                        this.enhancer?.recalcSlotWidth();
                        const target = restoreDate ?? this.periodStart(moment());
                        this.enhancer?.scrollToDate(target, restoreDate ? 0 : undefined);
                    }, 0);
                });
            })
            .catch(() => {
                this.ngZone.run(() => { this.loading = false; });
            });
    }

    private applyFilter() {
        const data = this.idSocietyVop > 0
            ? this.vehicles.filter(v => v.idSociety === this.idSocietyVop)
            : this.vehicles;
        this.buildResourcesFromData(data);
        this.buildEventsFromData(data);
    }

    // ── Resource builder ─────────────────────────────────────────────────────

    private buildResourcesFromData(data: any[]) {
        if (!this.ec) { return; }

        const stationMap = new Map<number, { name: string; vehicles: any[] }>();
        data.forEach(v => {
            const stationId   = v.idStation || 0;
            const stationName = v.stationName || (stationId ? `Station ${stationId}` : 'Non assigné');
            if (!stationMap.has(stationId)) {
                stationMap.set(stationId, { name: stationName, vehicles: [] });
            }
            stationMap.get(stationId)!.vehicles.push(v);
        });

        this.multiSociety = new Set(data.map(v => v.codeSociety).filter(Boolean)).size > 1;

        const resources: any[] = [];
        stationMap.forEach((station, stationId) => {
            resources.push({
                id: `station-${stationId}`,
                title: station.name,
                extendedProps: { isStation: true, stationId }
            });
            station.vehicles.forEach(v => {
                resources.push({
                    id: v.idVehicle,
                    title: v.vehicle || '',
                    parentId: `station-${stationId}`,
                    extendedProps: {
                        isVehicle:   true,
                        vehicleId:   v.idVehicle,
                        picture:     v.vehiclePicture || '',
                        brand:       v.brand       || '',
                        model:       v.model       || '',
                        codeSociety: v.codeSociety || ''
                    }
                });
            });
        });

        this.ec.setOption('resources', resources);
    }

    // ── Event builder ────────────────────────────────────────────────────────

    private buildEventsFromData(data: any[]) {
        if (!this.ec) { return; }

        const events: any[] = [];
        data.forEach(v => {
            (v.reservations || []).forEach((r: any) => {
                const isTarget = !!(this.idAccountTarget && r.idAccount === this.idAccountTarget);
                const opacity  = this.idAccountTarget ? (isTarget ? 1.0 : 0.3) : 1.0;
                const bgHex    = this.statusColor(r.status);
                const bg       = opacity < 1 ? this.hexToRgba(bgHex, opacity) : bgHex;
                const text     = this.statusTextColor(r.status, opacity);

                events.push({
                    id: `res-${r.idBooking || Math.random()}`,
                    resourceId: v.idVehicle,
                    start: r.begin,
                    end:   r.end,
                    title: '',
                    backgroundColor: bg,
                    textColor: text,
                    editable: false,
                    extendedProps: {
                        idAccount:     r.idAccount,
                        idReservation: r.idBooking,
                        code:          r.code     || '',
                        userName:      r.userName || '',
                        comment:       r.comment  || '',
                        status:        r.status   ?? null
                    }
                });
            });
        });

        // Background tint for station row lanes
        const windowEnd = moment(this.loadedWindowStart).add(WINDOW_DAYS, 'days').format('YYYY-MM-DD');
        const stationIds = new Set(data.map((v: any) => v.idStation || 0));
        stationIds.forEach(stationId => {
            events.push({
                id: `station-bg-${stationId}`,
                resourceId: `station-${stationId}`,
                start: this.loadedWindowStart,
                end: windowEnd,
                display: 'background',
                backgroundColor: 'transparent',
                classNames: ['station-row-sep'],
                editable: false
            });
        });

        this.loadedEvents = events;
        this.setCalendarEvents();
    }

    /**
     * Push the current event set to EventCalendar.
     * Always combines loadedEvents (reservations + static bg) with a freshly
     * generated past-day overlay so loadedEvents stays clean for lazy loading.
     */
    private setCalendarEvents(extraEvents: any[] = []): void {
        if (!this.ec) { return; }
        this.ec.setOption('events', [
            ...this.loadedEvents,
            ...this.buildPastOverlayEvents(),
            ...extraEvents
        ]);
    }

    /**
     * Generate past-day shading events on demand — one per vehicle row and one
     * per station row, spanning from the loaded window start to today midnight.
     * Not stored in loadedEvents; regenerated fresh on every call so the
     * cutoff date is always accurate and lazy loading need not manage them.
     */
    private buildPastOverlayEvents(): any[] {
        const todayMidnight = moment().startOf('day').format('YYYY-MM-DD');
        const pastBg = getComputedStyle(this.calendarEl.nativeElement)
            .getPropertyValue('--vop-past-day-bg').trim();
        const overlay: any[] = [];
        this.vehicles.forEach(v => {
            overlay.push({
                id: `past-overlay-${v.idVehicle}`,
                resourceId: v.idVehicle,
                start: this.loadedWindowStart,
                end: todayMidnight,
                display: 'background',
                backgroundColor: pastBg,
                editable: false
            });
        });
        const stationIds = new Set(this.vehicles.map((v: any) => v.idStation || 0));
        stationIds.forEach(stationId => {
            overlay.push({
                id: `past-overlay-station-${stationId}`,
                resourceId: `station-${stationId}`,
                start: this.loadedWindowStart,
                end: todayMidnight,
                display: 'background',
                backgroundColor: pastBg,
                editable: false
            });
        });
        return overlay;
    }

    // ── Colour helpers ───────────────────────────────────────────────────────

    private statusColor(status: number | undefined): string {
        if (status === undefined || status === null) { return '#CCCCCC'; }
        if (status >= 0  && status <= 2)  { return '#86E3BA'; }
        if (status >= 3  && status <= 10) { return '#F7A72A'; }
        if (status >= 11 && status <= 14) { return '#eeeeee'; }
        if (status === 20)                { return '#ff4949'; }
        return '#CCCCCC';
    }

    private statusTextColor(status: number | undefined, opacity: number): string {
        if (opacity < 1)                  { return '#444444'; }
        if (status === 20)                { return '#ffffff'; }
        if (status >= 3 && status <= 10)  { return '#ffffff'; }
        return '#444444';
    }

    private hexToRgba(hex: string, opacity: number): string {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r},${g},${b},${opacity})`;
    }

    // ── Zoom ─────────────────────────────────────────────────────────────────

    setZoom(level: ZoomLevel) {
        this.zoomLevel = level;
        if (this.enhancer) {
            this.enhancer.setZoom(level);
            // Delay scroll until EventCalendar finishes re-rendering after the
            // slotDuration change — otherwise EC resets scrollLeft after ours.
            setTimeout(() => {
                const target = this.periodStart(moment());
                this.enhancer?.scrollToDate(target, 0);
                this.currentDate = moment(target).format('YYYY-MM-DD');
            }, 0);
        }
    }

    // ── Navigation ───────────────────────────────────────────────────────────

    navigate(direction: 'prev' | 'next') {
        const start = moment(this.periodStart(moment(this.currentDate)));
        const sign  = direction === 'next' ? 1 : -1;
        // Month zoom uses calendar months so ±30 days doesn't cross boundaries correctly.
        const target = this.zoomLevel === 'month'
            ? start.add(sign, 'month').toDate()
            : start.add(ZOOM_CONFIGS[this.zoomLevel].navDays * sign, 'days').toDate();
        this.enhancer?.scrollToDate(target, 0);
        this.currentDate = moment(target).format('YYYY-MM-DD');
    }

    goToToday() {
        const target = this.periodStart(moment());
        this.enhancer?.scrollToDate(target, 0);
        this.currentDate = moment(target).format('YYYY-MM-DD');
    }

    /**
     * Return the period-start Date for the given moment (defaults to now).
     * day   → midnight of that day
     * week  → Monday 00:00 of that ISO week
     * month → 1st of that month at midnight
     */
    private periodStart(m?: moment.Moment): Date {
        const ref = m ?? moment();
        switch (this.zoomLevel) {
            case 'day':   return ref.clone().startOf('day').toDate();
            case 'week':  return ref.clone().startOf('isoWeek').toDate();
            case 'month': return ref.clone().startOf('month').toDate();
        }
    }

    // ── Title getter ─────────────────────────────────────────────────────────

    get titleFormatted(): string {
        const d = moment(this.currentDate);
        switch (this.zoomLevel) {
            case 'day':
                return d.locale('fr').format('dddd DD.MM.YYYY');
            case 'week':
                return `${d.clone().startOf('isoWeek').format('DD.MM.YYYY')} au ${d.clone().endOf('isoWeek').format('DD.MM.YYYY')} (S${d.isoWeek()})`;
            case 'month':
                return d.locale('fr').format('MMMM YYYY');
        }
    }

    // ── Slot selection & form ────────────────────────────────────────────────

    private onSlotSelected(info: any) {
        const vehicleId = info.resource.extendedProps.vehicleId as number;
        const vehicle   = this.vehicles.find(v => v.idVehicle === vehicleId);
        if (!vehicle) { return; }

        this.selection = {
            vehicleId,
            stationId:   vehicle.idStation   || 0,
            vehicleName: vehicle.vehicle     || '',
            stationName: vehicle.stationName || ''
        };
        this.formBegin   = this.toDateTimeLocal(info.start);
        this.formEnd     = this.toDateTimeLocal(info.end);
        this.submitError = '';
        this.addPendingEvent();

        // Always emit slotSelected — parent can listen regardless of mode.
        this.slotSelected.emit({
            idVehicle:   vehicleId,
            idStation:   this.selection.stationId,
            vehicleName: this.selection.vehicleName,
            stationName: this.selection.stationName,
            begin: moment(info.start).format('YYYY-MM-DDTHH:mm:ss'),
            end:   moment(info.end).format('YYYY-MM-DDTHH:mm:ss')
        });

        // In 'create' mode, open the embedded booking form.
        // The form overlays the canvas (no resize), so scroll explicitly so the
        // new event's start sits at the left edge with half-slot breathing room.
        if (this.mode === 'create') {
            this.showForm = true;
            setTimeout(() => {
                this.enhancer?.scrollToDate(this.fromDateTimeLocal(this.formBegin));
            }, 0);
        }
    }

    private addPendingEvent() {
        if (!this.ec || !this.selection || !this.formBegin || !this.formEnd) { return; }
        this.setCalendarEvents([{
            id: this.PENDING_EVENT_ID,
            resourceId: this.selection.vehicleId,
            start: this.fromDateTimeLocal(this.formBegin),
            end:   this.fromDateTimeLocal(this.formEnd),
            title: '',
            backgroundColor: '#176bc1',
            textColor: '#ffffff',
            editable: false
        }]);
    }

    clearSelection() {
        this.showForm    = false;
        this.selection   = null;
        this.formBegin   = '';
        this.formEnd     = '';
        this.formComment = '';
        this.submitError = '';
        if (this.ec) {
            try { this.ec.unselect(); } catch (e) {}
            this.setCalendarEvents();
        }
    }

    onBeginChange() { if (this.selection) { this.addPendingEvent(); } }
    onEndChange()   { if (this.selection) { this.addPendingEvent(); } }

    // ── Parent callbacks after API response ─────────────────────────────────

    /** Call after the API confirms creation. Reloads events and closes the form. */
    public notifyCreationSuccess() {
        const anchor = this.enhancer?.visibleDate();
        this.ngZone.run(() => {
            this.submitting = false;
            this.clearSelection();
            this.loadAll(anchor);
        });
    }

    /** Call when the API returns an error. Resets the spinner so the user can retry. */
    public notifyCreationError() {
        this.ngZone.run(() => { this.submitting = false; });
    }

    // ── Form submit ──────────────────────────────────────────────────────────

    get canSubmit(): boolean {
        return !!(this.selection && this.formBegin && this.formEnd && !this.submitting);
    }

    submit() {
        if (!this.canSubmit || !this.selection) { return; }

        const begin = this.fromDateTimeLocal(this.formBegin);
        const end   = this.fromDateTimeLocal(this.formEnd);

        if (begin >= end) {
            this.submitError = 'La date de fin doit être postérieure à la date de début.';
            return;
        }

        const overlap = this.loadedEvents.some((e: any) => {
            if (e.resourceId !== this.selection!.vehicleId) { return false; }
            const eStart = new Date(e.start).getTime();
            const eEnd   = new Date(e.end).getTime();
            return begin.getTime() < eEnd && end.getTime() > eStart;
        });
        if (overlap) {
            this.submitError = 'Ce créneau chevauche une réservation existante.';
            return;
        }

        const payload: CreatedReservation = {
            idVehicle: this.selection.vehicleId,
            idStation: this.selection.stationId,
            begin:     moment(begin).format('YYYY-MM-DDTHH:mm:ss'),
            end:       moment(end).format('YYYY-MM-DDTHH:mm:ss'),
            comment:   this.formComment || ''
        };
        if (this.idAccountTarget) { payload.idAccountTarget = this.idAccountTarget; }
        this.dateService.convertToISO(payload);

        this.submitting  = true;
        this.submitError = '';
        this.reservationCreated.emit(payload);
    }

    // ── Helpers ──────────────────────────────────────────────────────────────

    private toDateTimeLocal(date: Date | string): string {
        return moment(date).format('YYYY-MM-DDTHH:mm');
    }

    private fromDateTimeLocal(value: string): Date {
        return moment(value, 'YYYY-MM-DDTHH:mm').toDate();
    }
}
